import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { collection, getDocs, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { DEFAULT_PLANS } from "../hooks/usePlanSettings";
import { categoryLabel } from "../data/categories";
import { useLang } from "../i18n/LanguageContext";

export default function PlanAdmin() {
  const { t, lang } = useLang();
  const [prices, setPrices] = useState(null);
  const [standardPrices, setStandard] = useState(null);
  const [foundingLimit, setFoundingLimit] = useState(DEFAULT_PLANS.foundingLimit);
  const [count, setCount] = useState(0);
  const [savedMsg, setSavedMsg] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "plans"));
        if (snap.exists()) {
          const d = snap.data();
          setPrices({ ...DEFAULT_PLANS.prices, ...(d.prices || {}) });
          setStandard({ ...DEFAULT_PLANS.standardPrices, ...(d.standardPrices || {}) });
          setFoundingLimit(d.foundingLimit ?? DEFAULT_PLANS.foundingLimit);
        } else {
          setPrices(DEFAULT_PLANS.prices);
          setStandard(DEFAULT_PLANS.standardPrices);
        }
      } catch {
        setPrices(DEFAULT_PLANS.prices);
        setStandard(DEFAULT_PLANS.standardPrices);
      }
      try {
        const vs = await getDocs(collection(db, "vendors"));
        setCount(vs.size);
      } catch {}
    })();
  }, []);

  const groups = [
    { key: "default", label: t("plans.others") },
    { key: "salla", label: categoryLabel("salla", lang) },
    { key: "muzike", label: categoryLabel("muzike", lang) },
  ];

  const setCell = (which, setFn) => (key, tier, val) =>
    setFn((p) => ({ ...p, [key]: { ...(p[key] || p.default), [tier]: Number(val) || 0 } }));
  const setFounding = setCell("prices", setPrices);
  const setStd = setCell("standard", setStandard);

  const save = async () => {
    await setDoc(doc(db, "settings", "plans"), {
      foundingLimit: Number(foundingLimit) || 0,
      prices,
      standardPrices,
    });
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2500);
  };

  if (!prices || !standardPrices) return <Empty>…</Empty>;
  const foundingActive = count < foundingLimit;

  const table = (data, setter) => (
    <>
      <HeadRow>
        <span />
        <span>{t("plan.basic")}</span>
        <span>{t("plan.premium")}</span>
      </HeadRow>
      {groups.map((g) => {
        const row = data[g.key] || data.default;
        return (
          <PriceRow key={g.key}>
            <label>{g.label}</label>
            <div className="in">€<input type="number" min="0" step="0.01" value={row.basic} onChange={(e) => setter(g.key, "basic", e.target.value)} /></div>
            <div className="in">€<input type="number" min="0" step="0.01" value={row.premium} onChange={(e) => setter(g.key, "premium", e.target.value)} /></div>
          </PriceRow>
        );
      })}
    </>
  );

  return (
    <>
      <Counter $full={!foundingActive}>
        {count}/{foundingLimit} {t("plans.founding")} ·{" "}
        <b>{foundingActive ? t("plans.activeFounding") : t("plans.activeStandard")}</b>
      </Counter>

      <Panel>
        <h3>{t("plans.foundingPrices")}</h3>
        {table(prices, setFounding)}
      </Panel>

      <Panel>
        <h3>{t("plans.standard")}</h3>
        {table(standardPrices, setStd)}
      </Panel>

      <Panel>
        <Limit>
          <label>{t("plans.limit")}</label>
          <input type="number" min="0" value={foundingLimit} onChange={(e) => setFoundingLimit(e.target.value)} />
        </Limit>
        <SaveRow>
          <button onClick={save}>{t("admin.save")}</button>
          {savedMsg && <span>{t("manage.saved")}</span>}
        </SaveRow>
        <Hint>{t("plans.hint")}</Hint>
      </Panel>
    </>
  );
}

const Counter = styled.div`
  font-size: 14px;
  color: ${({ $full, theme }) => ($full ? theme.colors.rose : theme.colors.green)};
  margin-bottom: 16px;
  b { font-weight: 600; }
`;

const Panel = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 14px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  h3 { font-family: ${({ theme }) => theme.font.display}; font-weight: 500; font-size: 18px; }
`;

const HeadRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const PriceRow = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr;
  gap: 12px;
  align-items: center;
  label { font-size: 14px; font-weight: 500; }
  .in { display: flex; align-items: center; gap: 6px; color: ${({ theme }) => theme.colors.inkSoft}; }
  input { width: 100%; font-family: inherit; font-size: 14px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 10px; padding: 9px 11px; background: ${({ theme }) => theme.colors.cream}; outline: none; }
`;

const Limit = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  label { font-size: 13.5px; color: ${({ theme }) => theme.colors.inkSoft}; }
  input { width: 90px; font-family: inherit; font-size: 14px; border: 1px solid ${({ theme }) => theme.colors.hair}; border-radius: 10px; padding: 9px 11px; background: ${({ theme }) => theme.colors.cream}; outline: none; }
`;

const SaveRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  button { background: ${({ theme }) => theme.colors.green}; color: ${({ theme }) => theme.colors.cream}; border: none; border-radius: 100px; padding: 10px 22px; font-size: 14px; font-weight: 500; cursor: pointer; }
  span { color: ${({ theme }) => theme.colors.green}; font-size: 13px; font-weight: 500; }
`;

const Hint = styled.p`
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.inkSoft};
  line-height: 1.5;
`;

const Empty = styled.div`
  padding: 30px 0;
  text-align: center;
  color: ${({ theme }) => theme.colors.inkSoft};
`;
